if (customElements.get('facet-filters-form') === undefined) {
  class FacetFiltersForm extends HTMLElement {
    constructor() {
      super();
      this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

      this.debouncedOnSubmit = debounce((event) => {
        this.onSubmitHandler(event);
      }, 250);

      const facetForm = this.querySelector('form');
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

      const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
      if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

      // Add hover event listeners for desktop devices
      if (this.isDesktop()) {
        facetForm.addEventListener('mouseover', this.onFacetOptionHover.bind(this));
      }
    }

    // Utility method to check if the device is a desktop
    isDesktop() {
      return !('ontouchstart' in window || navigator.maxTouchPoints > 0);
    }

    // Event handler for hovering over facet options
    onFacetOptionHover(event) {
      let target = event.target;
      if (target.closest('label')) {
        target = target.closest('label').querySelector('input');
      }
      if (target && target.matches('input[type="checkbox"], input[type="radio"]')) {
        // Debounce to prevent excessive preloading
        if (this.hoverTimeout) {
          clearTimeout(this.hoverTimeout);
        }
        this.hoverTimeout = setTimeout(() => {
          const form = event.target.closest('form');
          const searchParams = this.createSearchParamsOnHover(form, target);
          this.preloadFacetData(searchParams);
        }, 50); // Adjust debounce time as needed
      }
    }

    createSearchParamsOnHover(form, target) {
      const forms = [];
      const isMobile = form.id === 'FacetFiltersFormMobile';

      const sortFilterForms = document.querySelectorAll('facet-filters-form form');
      sortFilterForms.forEach((formElement) => {
        if (!isMobile) {
          if (
            formElement.id === 'FacetSortForm' ||
            formElement.id === 'FacetFiltersForm' ||
            formElement.id === 'FacetSortDrawerForm'
          ) {
            forms.push(formElement);
          }
        } else if (formElement.id === 'FacetFiltersFormMobile') {
          forms.push(formElement);
        }
      });

      const searchParamsArray = forms.map((formElement) => {
        const formData = new FormData(formElement);

        // Apply the simulated selection change if this is the hovered form
        if (formElement === form) {
          const name = target.name;
          const value = target.value;
          const type = target.type;

          if (type === 'checkbox') {
            const values = formData.getAll(name);
            if (!values.includes(value)) {
              formData.append(name, value);
            }
          } else if (type === 'radio') {
            formData.set(name, value);
          }
        }

        // Convert FormData to array and sort it
        const formDataEntries = Array.from(formData.entries());
        formDataEntries.sort((a, b) => a[0].localeCompare(b[0]));

        return new URLSearchParams(formDataEntries).toString();
      });

      return searchParamsArray.join('&');
    }

    // Preload data for the hovered facet option and store it in the cache
    preloadFacetData(searchParams) {
      const sections = FacetFiltersForm.getSections();
      sections.forEach((section) => {
        const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
        if (!FacetFiltersForm.filterData[url]) {
          // Start fetching and store the promise in the cache
          FacetFiltersForm.filterData[url] = {
            promise: fetch(url)
              .then((response) => response.text())
              .then((html) => {
                FacetFiltersForm.filterData[url].html = html;
                FacetFiltersForm.filterData[url].promise = null;
                return html;
              }),
          };
        }
      });
    }

    static setListeners() {
      const onHistoryChange = (event) => {
        const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
        if (searchParams === FacetFiltersForm.searchParamsPrev) return;
        FacetFiltersForm.renderPage(searchParams, null, false);
      };
      window.addEventListener('popstate', onHistoryChange);
    }

    static toggleActiveFacets(disable = true) {
      document.querySelectorAll('.js-facet-remove').forEach((element) => {
        element.classList.toggle('disabled', disable);
      });
    }

    static renderPage(searchParams, event, updateURLHash = true) {
      FacetFiltersForm.searchParamsPrev = searchParams;
      const sections = FacetFiltersForm.getSections();
      const countContainer = document.getElementById('ProductCount');
      const countContainerDesktop = document.getElementById('ProductCountDesktop');
      const loadingSpinners = document.querySelectorAll(
        '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
      );
      loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
      document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
      if (countContainer) {
        countContainer.classList.add('loading');
      }
      if (countContainerDesktop) {
        countContainerDesktop.classList.add('loading');
      }

      sections.forEach((section) => {
        const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
        if (FacetFiltersForm.filterData[url]) {
          FacetFiltersForm.renderSectionFromCache(url, event);
        } else {
          FacetFiltersForm.renderSectionFromFetch(url, event);
        }
      });

      // scroll page to the top of where ProductGridContainer is if not in viewport
      const container = document.getElementById('ProductGridContainer');
      if (window.scrollY > container.getBoundingClientRect().top) {
        if (event?.inputType !== 'insertText') {
          window.scrollTo(0, container.getBoundingClientRect().top);
        }
      }

      if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    }

    // Modify the cache to handle in-progress promises
    static renderSectionFromFetch(url, event) {
      if (!FacetFiltersForm.filterData[url]) {
        // Start fetching and store the promise
        FacetFiltersForm.filterData[url] = {
          promise: fetch(url)
            .then((response) => response.text())
            .then((html) => {
              FacetFiltersForm.filterData[url].html = html;
              FacetFiltersForm.filterData[url].promise = null;
              return html;
            }),
        };
      }

      FacetFiltersForm.filterData[url].promise.then((html) => {
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
    }

    static renderSectionFromCache(url, event) {
      const data = FacetFiltersForm.filterData[url];
      if (data.html) {
        const html = data.html;
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      } else if (data.promise) {
        data.promise.then((html) => {
          FacetFiltersForm.renderFilters(html, event);
          FacetFiltersForm.renderProductGridContainer(html);
          FacetFiltersForm.renderProductCount(html);
          if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
        });
      }
    }

    static renderProductGridContainer(html) {
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
      window.scrambleTargets(parsedHTML);
      document.getElementById('ProductGridContainer').innerHTML = parsedHTML.innerHTML;

      document
        .getElementById('ProductGridContainer')
        .querySelectorAll('.scroll-trigger')
        .forEach((element) => {
          element.classList.add('scroll-trigger--cancel');
        });
    }

    static renderProductCount(html) {
      const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
      const container = document.getElementById('ProductCount');
      const containerDesktop = document.getElementById('ProductCountDesktop');
      container.innerHTML = count;
      container.classList.remove('loading');
      if (containerDesktop) {
        containerDesktop.innerHTML = count;
        containerDesktop.classList.remove('loading');
      }
      const loadingSpinners = document.querySelectorAll(
        '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
      );
      loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
    }

    static renderFilters(html, event) {
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
        '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
      );
      const facetDetailsElementsFromDom = document.querySelectorAll(
        '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
      );

      // Remove facets that are no longer returned from the server
      Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
        if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
          currentElement.remove();
        }
      });

      const matchesId = (element) => {
        const jsFilter = event ? event.target.closest('.js-filter') : undefined;
        return jsFilter ? element.id === jsFilter.id : false;
      };

      const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
      const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

      facetsToRender.forEach((elementToRender, index) => {
        const currentElement = document.getElementById(elementToRender.id);
        // Element already rendered in the DOM so just update the innerHTML
        if (currentElement) {
          document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
        } else {
          if (index > 0) {
            const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
            // Same facet type (eg horizontal/vertical or drawer/mobile)
            if (elementToRender.className === previousElementClassName) {
              document.getElementById(previousElementId).after(elementToRender);
              return;
            }
          }

          if (elementToRender.parentElement) {
            document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
          }
        }

        if (currentElement && typeof currentElement.initEvents === 'function') {
          currentElement.initEvents();
        }
      });

      FacetFiltersForm.renderActiveFacets(parsedHTML);
      FacetFiltersForm.renderAdditionalElements(parsedHTML);

      if (countsToRender) {
        const closestJSFilterID = event.target.closest('.js-filter').id;

        if (closestJSFilterID) {
          FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
          FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

          const newFacetDetailsElement = document.getElementById(closestJSFilterID);
          const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
            ? `.mobile-facets__close-button`
            : `.facets__summary`;
          const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

          const isTextInput = event.target.getAttribute('type') === 'text';

          if (newElementToActivate && !isTextInput) newElementToActivate.focus();
        }
      }
    }

    static renderActiveFacets(html) {
      const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

      activeFacetElementSelectors.forEach((selector) => {
        const activeFacetsElement = html.querySelector(selector);
        if (!activeFacetsElement) return;
        document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
      });

      FacetFiltersForm.toggleActiveFacets(false);
    }

    static renderAdditionalElements(html) {
      const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

      mobileElementSelectors.forEach((selector) => {
        if (!html.querySelector(selector)) return;
        document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
      });

      document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
    }

    static renderCounts(source, target) {
      const targetSummary = target.querySelector('.facets__summary');
      const sourceSummary = source.querySelector('.facets__summary');

      if (sourceSummary && targetSummary) {
        targetSummary.outerHTML = sourceSummary.outerHTML;
      }

      const targetHeaderElement = target.querySelector('.facets__header');
      const sourceHeaderElement = source.querySelector('.facets__header');

      if (sourceHeaderElement && targetHeaderElement) {
        targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
      }

      const targetWrapElement = target.querySelector('.facets-wrap');
      const sourceWrapElement = source.querySelector('.facets-wrap');

      if (sourceWrapElement && targetWrapElement) {
        const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
        if (isShowingMore) {
          sourceWrapElement
            .querySelectorAll('.facets__item.hidden')
            .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
        }

        targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
      }

      if (target && typeof target.initEvents === 'function') {
        target.initEvents();
      } else {
        const innerDetails = target.querySelector('details');
        if (innerDetails && typeof innerDetails.initEvents === 'function') {
          innerDetails.initEvents();
        }
      }
    }

    static renderMobileCounts(source, target) {
      const targetFacetsList = target.querySelector('.mobile-facets__list');
      const sourceFacetsList = source.querySelector('.mobile-facets__list');

      if (sourceFacetsList && targetFacetsList) {
        targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
      }
    }

    static updateURLHash(searchParams) {
      history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    }

    static getSections() {
      return [
        {
          section: document.getElementById('product-grid').dataset.id,
        },
      ];
    }

    createSearchParams(form) {
      const formData = new FormData(form);

      // Convert FormData to array and sort it
      const formDataEntries = Array.from(formData.entries());
      formDataEntries.sort((a, b) => a[0].localeCompare(b[0]));

      return new URLSearchParams(formDataEntries).toString();
    }

    onSubmitForm(searchParams, event) {
      FacetFiltersForm.renderPage(searchParams, event);
    }

    onSubmitHandler(event) {
      event.preventDefault();
      const sortFilterForms = document.querySelectorAll('facet-filters-form form');
      if (event.srcElement.className == 'mobile-facets__checkbox') {
        const searchParams = this.createSearchParams(event.target.closest('form'));
        this.onSubmitForm(searchParams, event);
      } else {
        const forms = [];
        const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

        sortFilterForms.forEach((form) => {
          if (!isMobile) {
            if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
              forms.push(this.createSearchParams(form));
            }
          } else if (form.id === 'FacetFiltersFormMobile') {
            forms.push(this.createSearchParams(form));
          }
        });
        this.onSubmitForm(forms.join('&'), event);
      }

      this.resetCollectionGridController();
    }

    onActiveFilterClick(event) {
      event.preventDefault();
      FacetFiltersForm.toggleActiveFacets();
      const url =
        event.currentTarget.href.indexOf('?') == -1
          ? ''
          : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
      FacetFiltersForm.renderPage(url);
    }

    resetCollectionGridController() {
      const collectionGridController = document.querySelector('collection-grid-controller');
      if (collectionGridController) {
        collectionGridController?.handle2XClick();
      }
    }
  }
  customElements.define('facet-filters-form', FacetFiltersForm);

  // Initialize the cache as an object to store promises and HTML
  FacetFiltersForm.filterData = {};
  FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
  FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
  FacetFiltersForm.setListeners();
}
if (customElements.get('price-range') === undefined) {
  class PriceRange extends HTMLElement {
    constructor() {
      super();
      this.querySelectorAll('input').forEach((element) => {
        element.addEventListener('change', this.onRangeChange.bind(this));
        element.addEventListener('keydown', this.onKeyDown.bind(this));
      });
      this.setMinAndMaxValues();
    }

    onRangeChange(event) {
      this.adjustToValidValues(event.currentTarget);
      this.setMinAndMaxValues();
    }

    onKeyDown(event) {
      if (event.metaKey) return;

      const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
      if (!event.key.match(pattern)) event.preventDefault();
    }

    setMinAndMaxValues() {
      const inputs = this.querySelectorAll('input');
      const minInput = inputs[0];
      const maxInput = inputs[1];
      if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
      if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
      if (minInput.value === '') maxInput.setAttribute('data-min', 0);
      if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
    }

    adjustToValidValues(input) {
      const value = Number(input.value);
      const min = Number(input.getAttribute('data-min'));
      const max = Number(input.getAttribute('data-max'));

      if (value < min) input.value = min;
      if (value > max) input.value = max;
    }
  }

  customElements.define('price-range', PriceRange);
}

if (customElements.get('facet-remove') === undefined) {
  class FacetRemove extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.closeFilter.bind(this);
      this.onKeyUp = this.onKeyUp.bind(this);
    }

    connectedCallback() {
      this.facetLink = this.querySelector('a');
      if (this.facetLink) {
        this.facetLink.setAttribute('role', 'button');
        this.facetLink.addEventListener('click', this.onClick);
        this.facetLink.addEventListener('keyup', this.onKeyUp);
      }
    }

    disconnectedCallback() {
      if (this.facetLink) {
        this.facetLink.removeEventListener('click', this.onClick);
        this.facetLink.removeEventListener('keyup', this.onKeyUp);
      }
    }

    onKeyUp(event) {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    }

    closeFilter(event) {
      event.preventDefault();
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      form.onActiveFilterClick(event);
    }
  }

  customElements.define('facet-remove', FacetRemove);
}
